import { useCallback, useEffect, useRef, useState } from 'react'

const EDGE_HIDE_PX = 4
const EDGE_SHOW_PX = 24

const HorizontalScrollCards = ({
  className = '',
  children,
  showNavArrows = false,
  onEndReached,
  ...props
}) => {
  const scrollerRef = useRef(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const updateScrollHint = useCallback(() => {
    const el = scrollerRef.current
    if (!el) return

    const maxScroll = el.scrollWidth - el.clientWidth
    const hasOverflow = maxScroll > 1
    const remaining = hasOverflow ? maxScroll - el.scrollLeft : 0

    setCanScrollLeft(hasOverflow && el.scrollLeft > EDGE_SHOW_PX)
    setCanScrollRight((wasShowing) => {
      if (!hasOverflow) return false
      // Hysteresis so the right-edge hint cannot toggle every pixel near the end.
      if (wasShowing) return remaining > EDGE_HIDE_PX
      return remaining > EDGE_SHOW_PX
    })
  }, [])

  useEffect(() => {
    const el = scrollerRef.current
    if (!el) return undefined

    let frame = 0
    const scheduleUpdate = () => {
      if (frame) return
      frame = window.requestAnimationFrame(() => {
        frame = 0
        updateScrollHint()
      })
    }

    updateScrollHint()
    el.addEventListener('scroll', scheduleUpdate, { passive: true })

    const resizeObserver = new ResizeObserver(scheduleUpdate)
    resizeObserver.observe(el)

    const mutationObserver = new MutationObserver(scheduleUpdate)
    mutationObserver.observe(el, { childList: true, subtree: false })

    return () => {
      el.removeEventListener('scroll', scheduleUpdate)
      resizeObserver.disconnect()
      mutationObserver.disconnect()
      if (frame) window.cancelAnimationFrame(frame)
    }
  }, [updateScrollHint])

  const handleScrollLeft = () => {
    const el = scrollerRef.current
    if (!el || !canScrollLeft) return
    el.scrollBy({ left: -Math.max(el.clientWidth * 0.75, 200), behavior: 'smooth' })
  }

  const handleScrollRight = () => {
    const el = scrollerRef.current
    if (!el) return
    const maxScroll = el.scrollWidth - el.clientWidth
    const remaining = maxScroll - el.scrollLeft
    if (remaining <= EDGE_SHOW_PX) {
      onEndReached?.()
      return
    }
    if (!canScrollRight) return
    el.scrollBy({ left: Math.max(el.clientWidth * 0.75, 200), behavior: 'smooth' })
  }

  return (
    <div className={`topPostsScrollHost${canScrollRight ? ' topPostsScrollHost--hint' : ''}`}>
      {showNavArrows && (
        <button
          type="button"
          className="topPostsScrollHint topPostsScrollHint--left"
          onClick={handleScrollLeft}
          disabled={!canScrollLeft}
          aria-label="Scroll to previous cards"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
      )}
      <div className="topPostsScrollTrack">
        <div ref={scrollerRef} className={className} {...props}>
          {children}
        </div>
      </div>
      {showNavArrows && (
        <button
          type="button"
          className="topPostsScrollHint topPostsScrollHint--right"
          onClick={handleScrollRight}
          disabled={!canScrollRight && !onEndReached}
          aria-label="Scroll to see more cards"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M9 6l6 6-6 6" />
          </svg>
        </button>
      )}
    </div>
  )
}

export default HorizontalScrollCards
